'use client'

import SubscribeCard from '@/components/now/SubscribeCard'
import TrackedLink from '@/components/tracking/TrackedLink'
import LeanMark from '@/components/ui/LeanMark'
import LeanTimeline from '@/components/ui/LeanTimeline'
import ListRow, {
  LeanInline,
  LeanVisual,
  ROW_ABOVE,
  ROW_LINK,
  ROW_META,
  ROW_TITLE,
  ROW_TITLE_NARROW,
} from '@/components/ui/ListRow'
import {
  CompactRow,
  HeadLink,
  MoreButton,
  OverviewHead,
  PartHead,
  RisingMark,
  SideColumns,
} from '@/components/ui/Overview'
import PageHeader, { Menu } from '@/components/ui/PageHeader'
import { selectionCoverage } from '@/components/ui/SelectionLabel'
import TopicAvatar from '@/components/ui/TopicAvatar'
import { SIDE_FLOORS, sideList, sideRule } from '@/utils/coverageTrends'
import { storyUrl } from '@/utils/date'
import { storiesInWindow, ts } from '@/utils/feedWindow'
import moment from 'moment-timezone'
import Link from 'next/link'
import { useMemo, useState } from 'react'

// Today (2026-11-01, the concept Sam approved), one page shape with no tabs:
//
//   Stories
//     Most covered           the ten biggest stories of the last day
//     The left / the right   side by side: the stories each side covered
//       covered more         more (below)
//     More stories           the rest, one line each: ten, then all on a click
//   Storylines that moved    ongoing stories with a new chapter today
//   Rising this week         topics in far more news than usual
//
// The Top / Trending / Left / Right tabs are gone: a reader saw one list at
// a time and the side tabs, the page's point, sat behind a click. Trending
// is now the ▲ mark on storyline and topic rows.
//
// The side lists (Sam, 2026-11-01): a story qualifies when its own label is
// Leans or Mostly that side (the label the row prints, the story page
// prints and the newsletter prints: coverageFromCounts, mirroring the
// pipeline's bias_utils) and it has at least MIN_SIDE articles; ranked by
// that side's share of its left and right articles; at most SIDE_N. The
// outlet pool leans left, so the right's list is often short. That is the
// true shape of the coverage, not a bug to tune away; the line under the
// lists says so in numbers.

const TOP_N = 10 // ten, not five (Sam, 2026-11-01)
// More stories: ten more, then the rest behind "Show all" (Sam, 2026-11-03:
// 33 one-line rows was too long a page).
const MORE_N = 10
const MIN_SIDE = SIDE_FLOORS.story

// The story rows in the shape the shared side-list rule reads.
const asRow = (s) => ({
  story: s,
  lean: s.lean,
  leanKey: selectionCoverage(s)?.key,
  total: s.articleCount,
})
const sideStories = (stories, side) =>
  sideList(stories.map(asRow), side, MIN_SIDE).map((r) => r.story)

const sectionName = (c) =>
  c ? c.charAt(0).toUpperCase() + c.slice(1).replace(/[-_]/g, ' ') : ''

// What a story row says under its title: the storyline it is a chapter of
// ("2026 Midterm Elections"; no "Day 39 of", 2026-11-01), else its section.
function storyContext(story) {
  const sl = story.storyline
  if (sl) return sl.title
  return sectionName(story.category)
}

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`

export default function TodayPage({
  stories = [],
  storylines = [],
  rising = [],
  isGuest = true,
}) {
  const [section, setSection] = useState('all')
  const [allMore, setAllMore] = useState(false)
  // Read the clock once per mount, not on every render.
  const [now] = useState(() => Date.now())

  const visible = useMemo(() => storiesInWindow(stories, now), [stories, now])

  const sections = useMemo(() => {
    const seen = new Map()
    for (const s of visible)
      if (s.category) seen.set(s.category, (seen.get(s.category) || 0) + 1)
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c)
  }, [visible])

  const ranked = useMemo(
    () =>
      visible
        .filter((s) => section === 'all' || s.category === section)
        .sort((a, b) => b.articleCount - a.articleCount),
    [visible, section]
  )
  const top = ranked.slice(0, TOP_N)
  const left = useMemo(() => sideStories(ranked, 'left'), [ranked])
  const right = useMemo(() => sideStories(ranked, 'right'), [ranked])
  // The rest, without the stories the side lists just showed (2026-11-03).
  const rest = useMemo(() => {
    const shown = new Set([...left, ...right].map((s) => s.id))
    return ranked.slice(TOP_N).filter((s) => !shown.has(s.id))
  }, [ranked, left, right])

  // "Updated" is the newest article on the page, not when the page
  // rendered: revalidation runs every five minutes while coverage lands
  // four times a day, so a render clock overstates freshness.
  const updatedLabel = useMemo(() => {
    const newest = visible.reduce((m, s) => Math.max(m, ts(s.updatedAt)), 0)
    if (!newest) return null
    const m = moment(newest).tz('America/New_York')
    const sameDay = m.isSame(moment().tz('America/New_York'), 'day')
    return `${m.format(sameDay ? 'h:mma' : 'MMM D, h:mma')} ET`
  }, [visible])

  const sideNote = () =>
    sideRule({
      noun: 'story',
      plural: 'stories',
      floor: MIN_SIDE,
      left: left.length,
      right: right.length,
    })

  return (
    <div className="page-wide py-8 sm:py-10">
      {isGuest && <SubscribeCard location="now_top" standalone top />}

      <PageHeader
        title={moment().tz('America/New_York').format('dddd, MMMM Do')}
        subtitle="The biggest stories of the last 24 hours, and how each side covered them."
      />

      <section className="mt-8 sm:mt-10" aria-labelledby="today-stories">
        <OverviewHead
          kind="story"
          title={<span id="today-stories">Stories</span>}
          aside={
            <>
              {updatedLabel && (
                <span
                  className="inline-flex items-center gap-1.5 whitespace-nowrap font-sans text-xs text-muted-foreground"
                  title={`Newest article: ${updatedLabel}`}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full bg-foreground"
                    aria-hidden="true"
                  />
                  Updated {updatedLabel}
                </span>
              )}
              {sections.length > 1 && (
                <Menu
                  label="Section"
                  value={section}
                  onChange={setSection}
                  options={[
                    { id: 'all', label: 'All sections' },
                    ...sections.map((c) => ({ id: c, label: sectionName(c) })),
                  ]}
                />
              )}
            </>
          }
        />

        {/* The ten biggest, straight under the heading: no "Most covered"
            eyebrow or colour key (Sam, 2026-11-03: calmer). The bars carry
            their own labels. */}
        <ol>
          {top.map((s, i) => (
            <StoryRow key={s.id} story={s} rank={i + 1} />
          ))}
        </ol>
        {!top.length && (
          <p className="py-6 font-sans text-sm text-muted-foreground">
            No stories from the last day yet. Check back soon.
          </p>
        )}

        {top.length > 0 && (
          <div className="mt-9">
            <SideColumns
              left={left.map((s, i) => (
                <StoryRow key={s.id} story={s} rank={i + 1} narrow />
              ))}
              right={right.map((s, i) => (
                <StoryRow key={s.id} story={s} rank={i + 1} narrow />
              ))}
              empty={(side) => `No story leaned ${side} in the last day.`}
              rule={sideNote()}
            />
          </div>
        )}

        {rest.length > 0 && (
          <>
            <PartHead className="mt-10">More stories</PartHead>
            <ol>
              {(allMore ? rest : rest.slice(0, MORE_N)).map((s, i) => (
                <CompactRow
                  key={s.id}
                  rank={TOP_N + i + 1}
                  href={storyUrl(s) || '#'}
                  title={s.title}
                  mark={<LeanMark node={s} showLabel={false} />}
                  figure={s.articleCount}
                />
              ))}
            </ol>
            {!allMore && rest.length > MORE_N && (
              <MoreButton onClick={() => setAllMore(true)}>
                {`Show ${rest.length - MORE_N} more`}
              </MoreButton>
            )}
          </>
        )}
        <p className="mt-4 font-sans text-sm">
          <Link
            href="/archive"
            className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2"
          >
            Earlier days’ stories
          </Link>
        </p>
      </section>

      {(storylines.length > 0 || rising.length > 0) && (
        <div className="mt-14 grid gap-14 md:grid-cols-2 md:gap-12">
          {storylines.length > 0 && (
            <section>
              <OverviewHead
                kind="storyline"
                title="Storylines that moved"
                subtitle="Ongoing stories with news in the last 24 hours"
                aside={<HeadLink href="/storylines">All storylines</HeadLink>}
              />
              <ol>
                {storylines.map((sl, i) => (
                  <StorylineRow key={sl.slug} line={sl} rank={i + 1} />
                ))}
              </ol>
            </section>
          )}
          {rising.length > 0 && (
            <section>
              <OverviewHead
                kind="topic"
                title="Rising in the last 7 days"
                subtitle="People, organizations and places in the news far more than usual"
                aside={<HeadLink href="/topics">All topics</HeadLink>}
              />
              <ol>
                {rising.map((t) => (
                  <RisingRow key={t.slug} topic={t} />
                ))}
              </ol>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function StoryRow({ story, rank, narrow = false }) {
  const href = storyUrl(story) || '#'
  const sl = story.storyline
  return (
    <ListRow
      lead={rank}
      narrow={narrow}
      figure={story.articleCount}
      unit={story.articleCount === 1 ? 'article' : 'articles'}
      visual={<LeanVisual node={story} />}
    >
      <Link
        href={href}
        className={`block ${narrow ? ROW_TITLE_NARROW : ROW_TITLE} ${ROW_LINK}`}
      >
        {story.title}
      </Link>
      <div className={ROW_META}>
        <LeanInline node={story} />
        {sl ? (
          <TrackedLink
            href={`/storyline/${sl.slug}`}
            event="storyline_click"
            params={{
              source_surface: 'now_feed',
              storyline_slug: sl.slug,
              story_slug: story.slug,
            }}
            className={`${ROW_ABOVE} hidden min-w-0 truncate hover:text-foreground hover:underline sm:block`}
          >
            {storyContext(story)}
          </TrackedLink>
        ) : (
          story.category && (
            <span className="hidden sm:inline">{storyContext(story)}</span>
          )
        )}
      </div>
    </ListRow>
  )
}

function StorylineRow({ line, rank }) {
  return (
    <ListRow
      lead={rank}
      narrow
      visualKind="chart"
      figure={line.total}
      unit="last 7 days"
      visual={<LeanTimeline days={line.days} />}
    >
      <TrackedLink
        href={`/storyline/${line.slug}`}
        event="storyline_click"
        params={{ source_surface: 'now_moved', storyline_slug: line.slug }}
        className={`block ${ROW_TITLE_NARROW} ${ROW_LINK}`}
      >
        {line.title}
      </TrackedLink>
      {/* One line, cut short: these rows are a teaser for /storylines. */}
      <p className="mt-1.5 truncate font-sans text-xs text-muted-foreground">
        {line.rise && <RisingMark text={line.rise} className="mr-2" />}
        New: {line.latest}
      </p>
    </ListRow>
  )
}

function RisingRow({ topic }) {
  return (
    <ListRow
      lead={<TopicAvatar topic={topic} size="h-[34px] w-[34px] text-[11px]" />}
      leadKind="avatar"
      narrow
      visualKind="chart"
      figure={topic.count}
      unit="last 7 days"
      visual={<LeanTimeline days={topic.days} />}
    >
      <TrackedLink
        href={`/topic/${topic.slug}`}
        event="topic_click"
        params={{ topic_slug: topic.slug, source_surface: 'now_rising' }}
        className={`block ${ROW_TITLE_NARROW} ${ROW_LINK}`}
      >
        {topic.name}
      </TrackedLink>
      <div className={ROW_META}>
        <RisingMark text={topic.rise} />
        {topic.kind && <span>{sectionName(topic.kind)}</span>}
        <span className="sm:hidden">
          {count(topic.lean.left, 'left', 'left')} ·{' '}
          {count(topic.lean.right, 'right', 'right')}
        </span>
      </div>
    </ListRow>
  )
}
